/**
 * A loan + one of its borrowers → the prefill payload for /send/:linkType.
 *
 * Ported from tpo-portal-frontend `screens/LoanDrilldownScreen/loanBorrowerPrefill.js`,
 * with this repo's `getLoanBorrowerEntityName` swapped in for tpo's
 * `borrowerEntityName`.
 *
 * Richer than the submissions-queue version: a loan already knows the product,
 * purpose, amount and subject property, so a link sent from here can fill the
 * borrower's loan section too — not just their name and email.
 *
 * Keys are the public forms' own field names (tpo/screens/SendToBorrowerScreen/
 * sendToBorrowerConfig `borrowerFields` / `loanFields`). Extra keys are inert:
 * the send screen only renders the fields its link kind offers, and
 * `buildPrefill` filters against the server allowlist before minting. So the
 * same payload works whether the user ends up sending an application, a credit
 * auth, or both.
 */

import { getLoanBorrowerEntityName } from "../../utils/loanPipeline/getLoanBorrowerEntityName";

const isBlank = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

export const buildLoanBorrowerPrefill = (loan, borrower = {}) => {
  const property = loan?.subjectProperties?.[0];

  const prefill = {
    // Borrower
    firstName: borrower.firstName,
    middleName: borrower.middleName,
    lastName: borrower.lastName,
    nameSuffix: borrower.nameSuffix,
    emailAddress: borrower.emailAddress,
    phone: borrower.phone || borrower.phoneNumber,
    citizenshipStatus: borrower.citizenshipStatus,
    estimatedFICO: borrower.estimatedFICO,
    primaryAddress: borrower.primaryAddress || borrower.address,
    // The loan is made to the entity, so that name belongs to every borrower on it.
    entityName: getLoanBorrowerEntityName(loan) || loan?.entityName,
    // Loan — application links only; the credit auth offers no loan fields.
    loanProductType: loan?.loanProductType,
    loanPurpose: loan?.loanPurpose,
    baseLoanAmount: loan?.baseLoanAmount ?? loan?.requestedLoanAmount,
    expectedClosingDate: loan?.expectedClosingDate,
    subjectPropertyAddress: property?.address,
  };

  return Object.fromEntries(
    Object.entries(prefill).filter(([, v]) => !isBlank(v)),
  );
};

export default buildLoanBorrowerPrefill;
